import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "INCOME", "EXPENSE"];
const CAN_MANAGE = ["ORG_ADMIN", "ACCOUNTS"];

export default function ChartOfAccounts() {
  const { user } = useAuth();
  const canManage = CAN_MANAGE.includes(user?.role);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/gl/accounts");
      setAccounts(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleActive = async (account) => {
    try {
      await api.patch(`/gl/accounts/${account.id}`, { is_active: !account.is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const depth = (account) => {
    let d = 0;
    let current = account;
    while (current?.parent_id) {
      current = accounts.find((a) => a.id === current.parent_id);
      d += 1;
      if (!current) break;
    }
    return d;
  };

  return (
    <div className="space-y-6" data-testid="chart-of-accounts-page">
      <PageHeader
        title="Chart of Accounts"
        description="Organisation-wide ledger accounts shared across every company's books."
        action={canManage && <CreateAccountDialog accounts={accounts} onCreated={load} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && accounts.length === 0 ? (
          <EmptyState title="No accounts yet" testId="accounts-empty-state" />
        ) : (
          <Table>
            <TableHeader>
              <TableRow><TableHead>Code</TableHead><TableHead>Name</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {accounts.map((a) => (
                <TableRow key={a.id} data-testid={`account-row-${a.code}`}>
                  <TableCell className="font-mono text-xs text-foreground">{a.code}</TableCell>
                  <TableCell className="text-foreground" style={{ paddingLeft: `${depth(a) * 20 + 16}px` }}>
                    {a.is_group ? <span className="font-semibold">{a.name}</span> : a.name}
                  </TableCell>
                  <TableCell className="text-muted-foreground">{a.account_type}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch checked={a.is_active} disabled={!canManage} onCheckedChange={() => toggleActive(a)} data-testid={`account-active-toggle-${a.code}`} />
                      <Badge variant={a.is_active ? "default" : "secondary"}>{a.is_active ? "Active" : "Inactive"}</Badge>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateAccountDialog({ accounts, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ code: "", name: "", account_type: "ASSET", parent_id: "", is_group: false });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      await api.post("/gl/accounts", { ...form, parent_id: form.parent_id || null });
      toast.success(`Account ${form.code} created`);
      setForm({ code: "", name: "", account_type: "ASSET", parent_id: "", is_group: false });
      setOpen(false);
      onCreated?.();
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-account-button"><Plus className="h-4 w-4" /> New Account</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>New Ledger Account</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-account-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Code</Label>
              <Input required data-testid="account-code-input" value={form.code} onChange={set("code")} className="mt-1.5" placeholder="1180" />
              <FieldError error={errors.code} />
            </div>
            <div>
              <Label>Type</Label>
              <Select value={form.account_type} onValueChange={(v) => setForm({ ...form, account_type: v })}>
                <SelectTrigger data-testid="account-type-select" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{ACCOUNT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Name</Label>
            <Input required data-testid="account-name-input" value={form.name} onChange={set("name")} className="mt-1.5" />
            <FieldError error={errors.name} />
          </div>
          <div>
            <Label>Parent account (optional)</Label>
            <Select value={form.parent_id} onValueChange={(v) => setForm({ ...form, parent_id: v })}>
              <SelectTrigger data-testid="account-parent-select" className="mt-1.5"><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>{accounts.filter((a) => a.is_group).map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-account-submit-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
